import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import calApi from '../../../../api/calApi';
import { useBookingContext } from '../../../../Context';
import { logBookingError } from '../../../../loggingUtils';
import { Booking } from '../../../../types';
import { isBookingValid } from '../../../../utils';
import XIcon from '../../../icons/XIcon';
import { BookingDataView, BookingDataViewType } from './BookingDataView';
import { bookingStrings, str } from './strings';

const cancelStrings = bookingStrings.confirmation.cancel;

interface BookingCancellationProps {
    bookingUid: string;
    closeCancellation: () => void;
}

export const BookingCancellation = ({
    bookingUid,
    closeCancellation,
}: BookingCancellationProps) => {
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cancelBookingData, setCancelBookingData] = useState<Booking | null>(
        null
    );
    const [cancellationResult, setCancellationResult] =
        useState<Booking | null>(null);
    const bookerContext = useBookingContext();

    useEffect(() => {
        calApi
            .getBooking(bookingUid)
            .then((bookingData) => {
                if (isBookingValid(bookingData.status)) {
                    setCancelBookingData(bookingData);
                } else {
                    closeCancellation();
                }
            })
            .catch((err) => {
                logBookingError(
                    'Failed to get booking data (cancellation flow)',
                    {
                        errorMessage: err.message,
                        additionalInfoToLog: bookerContext,
                    }
                );
            });
    }, []);

    const onCancelClick = useCallback(
        async (cancellationReason: string) => {
            if (bookingUid) {
                try {
                    setIsLoading(true);

                    const res = await calApi.cancelBooking(
                        bookingUid,
                        cancellationReason
                    );
                    setCancellationResult(res);
                } catch (err: any) {
                    logBookingError('Failed to cancel booking', {
                        errorMessage: err.message,
                        additionalInfoToLog: bookerContext,
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        },
        [bookingUid]
    );

    if (!cancelBookingData) {
        return null;
    }

    if (cancellationResult) {
        return (
            <BookingDataView
                bookingData={cancelBookingData}
                title={str(cancelStrings.title)}
                svgIcon={<XIcon />}
                type={BookingDataViewType.CANCELED}
                onClose={closeCancellation}
            />
        );
    }

    return (
        <BookingDataView
            bookingData={cancelBookingData}
            title={str(cancelStrings.title)}
            svgIcon={<XIcon />}
            type={BookingDataViewType.CANCELLATION}
        >
            <Footer>
                <Label>{str(cancelStrings.reason)}</Label>
                <TextArea
                    value={reason}
                    name='reason'
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={str(cancelStrings.placeholder)}
                />
                <StyledButton
                    onClick={() => onCancelClick(reason)}
                    disabled={isLoading}
                    data-auto='booking-cancel-confirm-button'
                >
                    {isLoading
                        ? str(cancelStrings.cancelling)
                        : str(cancelStrings.cancelButton)}
                </StyledButton>
            </Footer>
        </BookingDataView>
    );
};

const Label = styled.label`
    text-align: start;
    font-size: 13px;
    margin-block-end: 8px;
    font-weight: 350;
`;

const TextArea = styled.textarea`
    && {
        background-color: transparent;
        border: 1px solid #c9cacd;
        border-radius: 4px;
        height: 100px;
        padding: 8px;
        box-sizing: border-box;
        resize: vertical;
        &::placeholder {
            color: #93959a;
            font-size: 13px;
        }
    }
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledButton = styled.button`
    height: 32px;
    width: fit-content;
    background-color: #000;
    color: #fff;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    margin-inline-start: auto;
    padding-inline: 16px;
    margin-block-start: 16px;
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
